/*
 * =====================================================================================
 * DISCLAIMER:
 * =====================================================================================
 * This localization file is a community contribution and is not officially maintained
 * by Clerk. It has been provided by the community and may not be fully aligned
 * with the current or future states of the main application. Clerk does not guarantee
 * the accuracy, completeness, or timeliness of the translations in this file.
 * Use of this file is at your own risk and discretion.
 * =====================================================================================
 */

import type { LocalizationResource } from '@clerk/shared/types';

export const ptPT: LocalizationResource = {
  locale: 'pt-PT',
  apiKeys: {
    action__add: 'Adicionar nova chave',
    action__search: 'Pesquisar chaves',
    copySecret: {
      formButtonPrimary__copyAndClose: 'Copiar e fechar',
      formHint: 'Por razões de segurança, não permitiremos que visualize novamente mais tarde.',
      formTitle: 'Copie a sua chave de API "{{name}}" agora',
    },
    createdAndExpirationStatus__expiresOn:
      "Criada em {{ createdDate | shortDate('pt-PT') }} • Expira em {{ expiresDate | longDate('pt-PT') }}",
    createdAndExpirationStatus__never: "Criada em {{ createdDate | shortDate('pt-PT') }} • Nunca expira",
    detailsTitle__emptyRow: 'Não foram encontradas chaves de API',
    formButtonPrimary__add: 'Criar chave',
    formFieldCaption__expiration__expiresOn: 'Expira em {{ date }}',
    formFieldCaption__expiration__never: 'Esta chave nunca expirará',
    formFieldOption__expiration__180d: '180 dias',
    formFieldOption__expiration__1d: '1 dia',
    formFieldOption__expiration__1y: '1 ano',
    formFieldOption__expiration__30d: '30 dias',
    formFieldOption__expiration__60d: '60 dias',
    formFieldOption__expiration__7d: '7 dias',
    formFieldOption__expiration__90d: '90 dias',
    formFieldOption__expiration__never: 'Nunca',
    formHint: 'Introduza um nome para gerar uma nova chave. Poderá revogá-la em qualquer altura.',
    formTitle: 'Adicionar nova chave de API',
    lastUsed__days: 'há {{days}} d',
    lastUsed__hours: 'há {{hours}} h',
    lastUsed__minutes: 'há {{minutes}} min',
    lastUsed__months: 'há {{months}} m',
    lastUsed__seconds: 'há {{seconds}} s',
    lastUsed__years: 'há {{years}} a',
    menuAction__revoke: 'Revogar chave',
    revokeConfirmation: {
      confirmationText: 'Revogar',
      formButtonPrimary__revoke: 'Revogar chave',
      formHint: 'Tem a certeza de que pretende eliminar esta chave secreta?',
      formTitle: 'Revogar a chave secreta "{{apiKeyName}}"?',
    },
  },
  backButton: 'Voltar',
  badge__activePlan: 'Ativo',
  badge__canceledEndsAt: "Cancelado • Termina em {{ date | shortDate('pt-PT') }}",
  badge__currentPlan: 'Plano atual',
  badge__default: 'Padrão',
  badge__endsAt: "Termina em {{ date | shortDate('pt-PT') }}",
  badge__expired: 'Expirado',
  badge__freeTrial: 'Período experimental',
  badge__otherImpersonatorDevice: 'Personificar outro dispositivo',
  badge__pastDueAt: "Em atraso desde {{ date | shortDate('pt-PT') }}",
  badge__pastDuePlan: 'Em atraso',
  badge__primary: 'Principal',
  badge__renewsAt: "Renova em {{ date | shortDate('pt-PT') }}",
  badge__requiresAction: 'Requer ação',
  badge__startsAt: "Começa em {{ date | shortDate('pt-PT') }}",
  badge__thisDevice: 'Este dispositivo',
  badge__trialEndsAt: "O período experimental termina em {{ date | shortDate('pt-PT') }}",
  badge__unverified: 'Não verificado',
  badge__upcomingPlan: 'Plano seguinte',
  badge__userDevice: 'Dispositivo do utilizador',
  badge__you: 'O utilizador',
  billing: {
    addPaymentMethod__label: 'Adicionar método de pagamento',
    alwaysFree: 'Sempre gratuito',
    annually: 'Anual',
    availableFeatures: 'Funcionalidades disponíveis',
    billedAnnually: 'Faturado anualmente',
    billedMonthlyOnly: 'Apenas faturação mensal',
    cancelFreeTrial: 'Cancelar período experimental',
    cancelFreeTrialAccessUntil:
      "O seu período experimental manter-se-á ativo até {{ date | longDate('pt-PT') }}. Depois disso, deixará de ter acesso às funcionalidades experimentais. Não lhe será cobrado qualquer valor.",
    cancelFreeTrialTitle: 'Cancelar o período experimental do plano {{plan}}?',
    cancelSubscription: 'Cancelar subscrição',
    cancelSubscriptionAccessUntil:
      "Poderá continuar a utilizar as funcionalidades do plano '{{plan}}' até {{ date | longDate('pt-PT') }}, após o que deixará de lhes ter acesso.",
    cancelSubscriptionNoCharge: 'Não lhe será cobrado qualquer valor por esta subscrição.',
    cancelSubscriptionPastDue:
      'A sua subscrição terminará imediatamente e perderá o acesso a todas as funcionalidades do plano. O montante em atraso ser-lhe-á cobrado na subscrição seguinte.',
    cancelSubscriptionTitle: 'Cancelar a subscrição {{plan}}?',
    cannotSubscribeMonthly:
      'Não pode subscrever este plano com pagamento mensal. Para subscrever este plano, terá de optar pelo pagamento anual.',
    cannotSubscribeUnrecoverable:
      'Não pode subscrever este plano. A sua subscrição atual é mais dispendiosa do que este plano.',
    checkout: {
      description__paymentSuccessful: 'O seu pagamento foi efetuado com sucesso.',
      description__subscriptionSuccessful: 'A sua nova subscrição está pronta.',
      downgradeNotice:
        'Manterá a sua subscrição atual e respetivas funcionalidades até ao fim do ciclo de faturação e, depois disso, passará para esta subscrição.',
      emailForm: {
        subtitle:
          'Antes de concluir a compra, tem de adicionar um endereço de e-mail para o qual serão enviadas as faturas.',
        title: 'Adicionar um endereço de e-mail',
      },
      lineItems: {
        title__freeTrialEndsAt: 'O período experimental termina em',
        title__paymentMethod: 'Método de pagamento',
        title__statementId: 'ID do extrato',
        title__subscriptionBegins: 'A subscrição começa em',
        title__totalPaid: 'Total pago',
      },
      pastDueNotice: 'A sua subscrição anterior encontrava-se em atraso, sem pagamento.',
      perMonth: 'por mês',
      title: 'Finalizar compra',
      title__paymentSuccessful: 'Pagamento efetuado com sucesso!',
      title__subscriptionSuccessful: 'Sucesso!',
      title__trialSuccess: 'Período experimental iniciado com sucesso!',
      totalDueAfterTrial: 'Total devido após o período experimental terminar em {{days}} dias',
    },
    credit: 'Crédito',
    creditRemainder: 'Crédito relativo ao período restante da sua subscrição atual.',
    defaultFreePlanActive: 'Está atualmente no plano Gratuito',
    free: 'Gratuito',
    getStarted: 'Começar',
    keepFreeTrial: 'Manter período experimental',
    keepSubscription: 'Manter subscrição',
    manage: 'Gerir',
    manageSubscription: 'Gerir subscrição',
    month: 'Mês',
    monthly: 'Mensal',
    pastDue: 'Em atraso',
    pay: 'Pagar {{amount}}',
    paymentMethod: {
      applePayDescription: {
        annual: 'Pagamento anual',
        monthly: 'Pagamento mensal',
      },
      dev: {
        anyNumbers: 'Quaisquer números',
        cardNumber: 'Número do cartão',
        cvcZip: 'CVC, código postal',
        developmentMode: 'Modo de desenvolvimento',
        expirationDate: 'Data de validade',
        testCardInfo: 'Informações do cartão de teste',
      },
    },
    paymentMethods__label: 'Métodos de pagamento',
    highlightedPlanBadge: 'Popular',
    pricingTable: {
      billingCycle: 'Ciclo de faturação',
      included: 'Incluído',
    },
    reSubscribe: 'Subscrever novamente',
    seeAllFeatures: 'Ver todas as funcionalidades',
    startFreeTrial: 'Iniciar período experimental',
    startFreeTrial__days: 'Iniciar período experimental de {{days}} dias',
    subscribe: 'Subscrever',
    subscriptionDetails: {
      beginsOn: 'Começa em',
      currentBillingCycle: 'Ciclo de faturação atual',
      endsOn: 'Termina em',
      firstPaymentAmount: 'Montante do primeiro pagamento',
      firstPaymentOn: 'Primeiro pagamento em',
      nextPaymentAmount: 'Montante do próximo pagamento',
      nextPaymentOn: 'Próximo pagamento em',
      pastDueAt: 'Em atraso desde',
      renewsAt: 'Renova em',
      subscribedOn: 'Subscrito em',
      title: 'Subscrição',
      trialEndsOn: 'O período experimental termina em',
      trialStartedOn: 'Período experimental iniciado em',
    },
    subtotal: 'Subtotal',
    switchPlan: 'Mudar para este plano',
    switchToAnnual: 'Mudar para anual',
    switchToAnnualWithAnnualPrice: 'Mudar para anual {{currency}}{{price}} / ano',
    switchToMonthly: 'Mudar para mensal',
    switchToMonthlyWithPrice: 'Mudar para mensal {{currency}}{{price}} / mês',
    totalDue: 'Total devido',
    totalDueToday: 'Total devido hoje',
    viewFeatures: 'Ver funcionalidades',
    viewPayment: 'Ver pagamento',
    year: 'Ano',
  },
  configureSSO: {
    missingManageEnterpriseConnectionsPermission: {
      subtitle: 'Contacte o administrador da sua organização para obter permissões para gerir ligações empresariais.',
      title: 'Não tem permissão para gerir ligações empresariais',
    },
    navbar: {
      title: 'Configurar autenticação única (SSO)',
    },
    selectProviderStep: {
      title: 'Selecionar fornecedor',
      subtitle: 'Selecione o fornecedor para o qual vai configurar o SSO.',
      body: {
        title: 'Selecione o seu fornecedor de identidade',
        description: 'Iremos guiá-lo pelo processo de configuração detalhado a seguir.',
      },
      saml: {
        groupLabel: 'SAML',
        okta: 'Okta Workforce',
        customSaml: 'Fornecedor SAML personalizado',
      },
      warning: 'Depois de um fornecedor ser selecionado não pode ser alterado até que a configuração esteja terminada',
    },
    verifyEmailDomainStep: {
      title: 'Verificar endereço de e-mail',
      subtitle: 'Verifique o endereço de e-mail no qual pretende ativar a ligação empresarial.',
      addEmailAddress: {
        formTitle: 'Precisamos do seu e-mail',
        formSubtitle: 'Para começar, precisaremos do seu endereço de e-mail',
        inputPlaceholder: 'name@company.com',
        inputLabel: 'Endereço de e-mail',
      },
      emailCode: {
        formTitle: 'Verifique o seu endereço de e-mail',
        formSubtitle: 'Introduza o código de verificação enviado para {{identifier}}',
        resendButton: 'Não recebeu um código? Reenviar',
        verified: {
          title: 'Recebemos o seu e-mail',
          subtitle: 'Verificou o seu endereço de e-mail com o seguinte e-mail',
          inputLabel: 'Endereço de e-mail verificado',
        },
      },
      domainTaken: {
        title: 'Este domínio ({{domain}}) já possui uma ligação SSO',
        subtitle: 'Contacte o administrador da aplicação para obter acesso através da ligação existente.',
      },
    },
  },
  createOrganization: {
    formButtonSubmit: 'Criar organização',
    invitePage: {
      formButtonReset: 'Ignorar',
    },
    title: 'Criar organização',
  },
  dates: {
    lastDay: "Ontem às {{ date | timeString('pt-PT') }}",
    next6Days: "{{ date | weekday('pt-PT','long') }} às {{ date | timeString('pt-PT') }}",
    nextDay: "Amanhã às {{ date | timeString('pt-PT') }}",
    numeric: "{{ date | numeric('pt-PT') }}",
    previous6Days: "Último {{ date | weekday('pt-PT','long') }} às {{ date | timeString('pt-PT') }}",
    sameDay: "Hoje às {{ date | timeString('pt-PT') }}",
  },
  dividerText: 'ou',
  footerActionLink__alternativePhoneCodeProvider: 'Enviar código por SMS em alternativa',
  footerActionLink__useAnotherMethod: 'Utilize outro método',
  footerPageLink__help: 'Ajuda',
  footerPageLink__privacy: 'Privacidade',
  footerPageLink__terms: 'Termos de utilização',
  formButtonPrimary: 'Continuar',
  formButtonPrimary__verify: 'Verificar',
  formFieldAction__forgotPassword: 'Esqueceu a palavra-passe?',
  formFieldError__matchingPasswords: 'As palavras-passe coincidem.',
  formFieldError__notMatchingPasswords: 'As palavras-passe não coincidem.',
  formFieldError__verificationLinkExpired: 'A ligação de verificação expirou. Solicite uma nova ligação.',
  formFieldHintText__optional: 'Opcional',
  formFieldHintText__slug:
    'Um slug é um identificador legível por humanos que tem de ser único. É frequentemente utilizado em URLs.',
  formFieldInputPlaceholder__apiKeyDescription: 'Explique por que motivo está a gerar esta chave',
  formFieldInputPlaceholder__apiKeyExpirationDate: 'Selecionar data',
  formFieldInputPlaceholder__apiKeyName: 'Introduza o nome da sua chave secreta',
  formFieldInputPlaceholder__backupCode: 'Insira o código de backup',
  formFieldInputPlaceholder__confirmDeletionUserAccount: 'Eliminar conta',
  formFieldInputPlaceholder__emailAddress: 'Insira o seu endereço de e-mail',
  formFieldInputPlaceholder__emailAddress_username: 'Insira o seu e-mail ou nome de utilizador',
  formFieldInputPlaceholder__emailAddresses: 'Insira um ou mais endereços de e-mail separados por espaços ou vírgulas',
  formFieldInputPlaceholder__firstName: 'Insira o seu primeiro nome',
  formFieldInputPlaceholder__lastName: 'Insira o seu apelido',
  formFieldInputPlaceholder__organizationDomain: 'Insira o domínio da organização',
  formFieldInputPlaceholder__organizationDomainEmailAddress: 'Insira o endereço de e-mail do domínio da organização',
  formFieldInputPlaceholder__organizationName: 'Insira o nome da organização',
  formFieldInputPlaceholder__organizationSlug: 'Insira o identificador da organização (slug)',
  formFieldInputPlaceholder__password: 'Insira a sua palavra-passe',
  formFieldInputPlaceholder__phoneNumber: 'Insira o seu número de telemóvel',
  formFieldInputPlaceholder__username: 'Insira o seu nome de utilizador',
  formFieldInput__emailAddress_format: 'Formato de exemplo: nome@exemplo.com',
  formFieldLabel__apiKey: 'Chave de API',
  formFieldLabel__apiKeyDescription: 'Descrição',
  formFieldLabel__apiKeyExpiration: 'Expiração',
  formFieldLabel__apiKeyName: 'Nome da chave secreta',
  formFieldLabel__automaticInvitations: 'Ativar convites automáticos para este domínio',
  formFieldLabel__backupCode: 'Código de backup',
  formFieldLabel__confirmDeletion: 'Confirmar exclusão',
  formFieldLabel__confirmPassword: 'Confirmar palavra-passe',
  formFieldLabel__currentPassword: 'Palavra-passe atual',
  formFieldLabel__emailAddress: 'Endereço de e-mail',
  formFieldLabel__emailAddress_username: 'E-mail ou nome de utilizador',
  formFieldLabel__emailAddresses: 'Endereços de e-mail',
  formFieldLabel__firstName: 'Nome',
  formFieldLabel__lastName: 'Apelido',
  formFieldLabel__newPassword: 'Nova palavra-passe',
  formFieldLabel__organizationDomain: 'Domínio',
  formFieldLabel__organizationDomainDeletePending: 'Eliminar convites e sugestões pendentes',
  formFieldLabel__organizationDomainEmailAddress: 'Endereço de e-mail de verificação',
  formFieldLabel__organizationDomainEmailAddressDescription:
    'Endereço de e-mail para receber um código e verificar este domínio',
  formFieldLabel__organizationName: 'Nome da organização',
  formFieldLabel__organizationSlug: 'Slug do URL',
  formFieldLabel__passkeyName: 'Nome da chave de acesso',
  formFieldLabel__password: 'Palavra-passe',
  formFieldLabel__phoneNumber: 'Telemóvel',
  formFieldLabel__role: 'Função',
  formFieldLabel__signOutOfOtherSessions: 'Terminar sessão em todos os outros dispositivos',
  formFieldLabel__username: 'Nome de utilizador',
  impersonationFab: {
    action__signOut: 'Terminar sessão',
    title: 'Sessão iniciada como {{identifier}}',
  },
  lastAuthenticationStrategy: 'Último uso',
  maintenanceMode: 'Modo de Manutenção',
  membershipRole__admin: 'Administrador',
  membershipRole__basicMember: 'Membro',
  membershipRole__guestMember: 'Convidado',
  organizationList: {
    action__createOrganization: 'Criar organização',
    action__invitationAccept: 'Participar',
    action__suggestionsAccept: 'Solicitar participação',
    createOrganization: 'Criar organização',
    invitationAcceptedLabel: 'A participar',
    subtitle: 'para continuar no {{applicationName}}',
    suggestionsAcceptedLabel: 'Aprovação pendente',
    title: 'Selecione uma conta',
    titleWithoutPersonal: 'Selecione uma organização',
  },
  organizationProfile: {
    apiKeysPage: {
      title: 'Chaves de API',
    },
    badge__automaticInvitation: 'Convites automáticos',
    badge__automaticSuggestion: 'Sugestões automáticas',
    badge__manualInvitation: 'Sem inscrição automática',
    badge__unverified: 'Não verificado',
    billingPage: {
      paymentHistorySection: {
        empty: 'Sem histórico de pagamentos',
        notFound: 'Tentativa de pagamento não encontrada',
        tableHeader__amount: 'Montante',
        tableHeader__date: 'Data',
        tableHeader__status: 'Estado',
      },
      paymentMethodsSection: {
        actionLabel__default: 'Definir como predefinido',
        actionLabel__remove: 'Remover',
        add: 'Adicionar novo método de pagamento',
        addSubtitle: 'Adicione um novo método de pagamento à sua conta.',
        cancelButton: 'Cancelar',
        formButtonPrimary__add: 'Adicionar método de pagamento',
        formButtonPrimary__pay: 'Pagar {{amount}}',
        payWithTestCardButton: 'Pagar com cartão de teste',
        removeMethod: {
          messageLine1: '{{identifier}} será removido desta conta.',
          messageLine2:
            'Deixará de poder utilizar este método de pagamento e quaisquer subscrições recorrentes que dele dependam deixarão de funcionar.',
          successMessage: '{{paymentMethod}} foi removido da sua conta.',
          title: 'Remover método de pagamento',
        },
        title: 'Métodos de pagamento',
      },
      start: {
        headerTitle__payments: 'Pagamentos',
        headerTitle__plans: 'Planos',
        headerTitle__statements: 'Extratos',
        headerTitle__subscriptions: 'Subscrição',
      },
      statementsSection: {
        empty: 'Sem extratos para apresentar',
        itemCaption__paidForPlan: 'Pago pelo plano {{plan}} {{period}}',
        itemCaption__proratedCredit: 'Crédito proporcional por utilização parcial da subscrição anterior',
        itemCaption__subscribedAndPaidForPlan: 'Subscrição e pagamento do plano {{plan}} {{period}}',
        notFound: 'Extrato não encontrado',
        tableHeader__amount: 'Montante',
        tableHeader__date: 'Data',
        title: 'Extratos',
        totalPaid: 'Total pago',
      },
      subscriptionsListSection: {
        actionLabel__manageSubscription: 'Gerir',
        actionLabel__newSubscription: 'Subscrever um plano',
        actionLabel__switchPlan: 'Mudar de plano',
        tableHeader__edit: 'Editar',
        tableHeader__plan: 'Plano',
        tableHeader__startDate: 'Data de início',
        title: 'Subscrição',
      },
      subscriptionsSection: {
        actionLabel__default: 'Gerir',
      },
      switchPlansSection: {
        title: 'Mudar de plano',
      },
      title: 'Faturação',
    },
    createDomainPage: {
      subtitle:
        'Adicione o domínio para verificar. Utilizadores com endereços de e-mail neste domínio podem entrar na organização automaticamente ou solicitar entrada.',
      title: 'Adicionar domínio',
    },
    invitePage: {
      detailsTitle__inviteFailed: 'Os convites não puderam ser enviados. Corrija o seguinte e tente novamente:',
      formButtonPrimary__continue: 'Enviar convites',
      selectDropdown__role: 'Selecionar função',
      subtitle: 'Convidar novos membros para esta organização',
      successMessage: 'Convites enviados com sucesso',
      title: 'Convidar membros',
    },
    membersPage: {
      action__invite: 'Convidar',
      action__search: 'Pesquisar',
      activeMembersTab: {
        menuAction__remove: 'Remover membro',
        tableHeader__actions: 'Ações',
        tableHeader__joined: 'Entrou',
        tableHeader__role: 'Função',
        tableHeader__user: 'Utilizador',
      },
      alerts: {
        roleSetMigrationInProgress: {
          subtitle:
            'Estamos a atualizar as funções disponíveis. Assim que terminar, poderá atualizar as funções novamente.',
          title: 'As funções estão temporariamente bloqueadas',
        },
      },
      detailsTitle__emptyRow: 'Nenhum membro para mostrar',
      invitationsTab: {
        autoInvitations: {
          headerSubtitle:
            'Convide utilizadores ligando um domínio de e-mail à sua organização. Qualquer pessoa que se registe com um domínio de e-mail correspondente poderá entrar na organização a qualquer momento.',
          headerTitle: 'Convites automáticos',
          primaryButton: 'Configurar domínios verificados',
        },
        table__emptyRow: 'Nenhum convite a mostrar',
      },
      invitedMembersTab: {
        menuAction__revoke: 'Cancelar convite',
        tableHeader__invited: 'Convidado',
      },
      requestsTab: {
        autoSuggestions: {
          headerSubtitle:
            'Utilizadores que se inscrevem com um domínio de e-mail correspondente podem ver uma sugestão para solicitar participação na sua organização.',
          headerTitle: 'Sugestões automáticas',
          primaryButton: 'Configurar domínios verificados',
        },
        menuAction__approve: 'Aprovar',
        menuAction__reject: 'Rejeitar',
        tableHeader__requested: 'Acesso solicitado',
        table__emptyRow: 'Nenhuma solicitação a mostrar',
      },
      start: {
        headerTitle__invitations: 'Convites',
        headerTitle__members: 'Membros',
        headerTitle__requests: 'Pedidos',
      },
    },
    navbar: {
      apiKeys: 'Chaves de API',
      billing: 'Faturação',
      description: 'Gira a sua organização.',
      general: 'Geral',
      members: 'Membros',
      title: 'Organização',
    },
    plansPage: {
      alerts: {
        noPermissionsToManageBilling: 'Não tem permissões para gerir a faturação.',
      },
      title: 'Planos',
    },
    profilePage: {
      dangerSection: {
        deleteOrganization: {
          actionDescription: 'Escreva {{organizationName}} abaixo para continuar.',
          messageLine1: 'Tem a certeza de que pretende eliminar esta organização?',
          messageLine2: 'Esta ação é permanente e irreversível.',
          successMessage: 'Eliminou a organização.',
          title: 'Eliminar organização',
        },
        leaveOrganization: {
          actionDescription: 'Escreva {{organizationName}} abaixo para continuar.',
          messageLine1:
            'Tem a certeza de que pretende sair desta organização? Perderá o acesso a esta organização e às respetivas aplicações.',
          messageLine2: 'Esta ação é permanente e não pode ser desfeita.',
          successMessage: 'Saiu da organização.',
          title: 'Sair da organização',
        },
        title: 'Perigo',
      },
      domainSection: {
        menuAction__manage: 'Gerir',
        menuAction__remove: 'Eliminar',
        menuAction__verify: 'Verificar',
        primaryButton: 'Adicionar domínio',
        subtitle:
          'Permita que os utilizadores entrem na organização automaticamente ou solicitem adesão com base num domínio de e-mail verificado.',
        title: 'Domínios verificados',
      },
      successMessage: 'A organização foi atualizada.',
      title: 'Perfil da organização',
    },
    removeDomainPage: {
      messageLine1: 'O domínio de e-mail {{domain}} será removido.',
      messageLine2: 'Os utilizadores não conseguirão entrar na organização após isso.',
      successMessage: '{{domain}} foi removido.',
      title: 'Eliminar domínio',
    },
    start: {
      headerTitle__general: 'Geral',
      headerTitle__members: 'Membros',
      profileSection: {
        primaryButton: 'Guardar',
        title: 'Perfil da organização',
        uploadAction__title: 'Logo',
      },
    },
    verifiedDomainPage: {
      dangerTab: {
        calloutInfoLabel: 'A exclusão deste domínio afetará os utilizadores convidados.',
        removeDomainActionLabel__remove: 'Eliminar domínio',
        removeDomainSubtitle: 'Remova este domínio dos seus domínios verificados',
        removeDomainTitle: 'Eliminar domínio',
      },
      enrollmentTab: {
        automaticInvitationOption__description:
          'Os utilizadores são automaticamente convidados a entrar na organização quando se inscrevem.',
        automaticInvitationOption__label: 'Convites automáticos',
        automaticSuggestionOption__description:
          'Os utilizadores recebem uma sugestão para solicitar participação, mas devem ser aprovados por um administrador antes de poderem entrar na organização.',
        automaticSuggestionOption__label: 'Sugestões automáticas',
        calloutInfoLabel: 'Alterar o modo de inscrição afetará apenas os novos utilizadores.',
        calloutInvitationCountLabel: 'Convites pendentes enviados aos utilizadores: {{count}}',
        calloutSuggestionCountLabel: 'Sugestões pendentes enviadas aos utilizadores: {{count}}',
        manualInvitationOption__description: 'Os utilizadores só podem ser convidados manualmente para a organização.',
        manualInvitationOption__label: 'Sem inscrição automática',
        subtitle: 'Escolha como os utilizadores deste domínio se podem entrar na organização.',
      },
      start: {
        headerTitle__danger: 'Perigo',
        headerTitle__enrollment: 'Opções de inscrição',
      },
      subtitle: 'O domínio {{domain}} agora está verificado. Continue por selecionar o modo de inscrição.',
      title: 'Atualizar {{domain}}',
    },
    verifyDomainPage: {
      formSubtitle: 'Insira o código de verificação enviado para o seu endereço de e-mail',
      formTitle: 'Código de verificação',
      resendButton: 'Não recebeu um código? Reenviar',
      subtitle: 'O domínio {{domainName}} precisa ser verificado por e-mail.',
      subtitleVerificationCodeScreen:
        'Um código de verificação foi enviado para {{emailAddress}}. Insira-o para continuar.',
      title: 'Verificar domínio',
    },
  },
  organizationSwitcher: {
    action__closeOrganizationSwitcher: 'Fechar seletor de organizações',
    action__createOrganization: 'Criar organização',
    action__invitationAccept: 'Participar',
    action__manageOrganization: 'Configurar organização',
    action__openOrganizationSwitcher: 'Abrir seletor de organizações',
    action__suggestionsAccept: 'Solicitar participação',
    notSelected: 'Nenhuma organização selecionada',
    personalWorkspace: 'Conta pessoal',
    suggestionsAcceptedLabel: 'Aprovação pendente',
  },
  paginationButton__next: 'Próximo',
  paginationButton__previous: 'Anterior',
  paginationRowText__displaying: 'Apresentando',
  paginationRowText__of: 'de',
  reverification: {
    alternativeMethods: {
      actionLink: 'Obter ajuda',
      actionText: 'Não tem nenhum destes métodos?',
      blockButton__backupCode: 'Utilizar um código de recuperação',
      blockButton__emailCode: 'Código por e-mail para {{identifier}}',
      blockButton__passkey: 'Utilizar a sua chave de acesso',
      blockButton__password: 'Continuar com a sua palavra-passe',
      blockButton__phoneCode: 'Enviar código SMS para {{identifier}}',
      blockButton__totp: 'Utilizar a sua aplicação autenticadora',
      getHelp: {
        blockButton__emailSupport: 'Enviar e-mail ao suporte',
        content:
          'Se tiver dificuldades em verificar a sua conta, envie-nos um e-mail e faremos o possível para restaurar o acesso com a maior brevidade.',
        title: 'Obter ajuda',
      },
      subtitle: 'Está com dificuldades? Pode utilizar qualquer um destes métodos para verificação.',
      title: 'Utilizar outro método',
    },
    backupCodeMfa: {
      subtitle: 'Introduza o código de recuperação que recebeu ao configurar a autenticação em duas etapas',
      title: 'Introduza um código de recuperação',
    },
    emailCode: {
      formTitle: 'Código de verificação',
      resendButton: 'Não recebeu um código? Reenviar',
      subtitle: 'Introduza o código enviado para o seu e-mail para continuar',
      title: 'Verificação necessária',
    },
    noAvailableMethods: {
      message: 'Não é possível prosseguir com a verificação. Não existe um fator de autenticação adequado configurado.',
      subtitle: 'Ocorreu um erro',
      title: 'Não é possível verificar a sua conta',
    },
    passkey: {
      blockButton__passkey: 'Utilizar a sua chave de acesso',
      subtitle:
        'A utilização da sua chave de acesso confirma a sua identidade. O dispositivo pode pedir a sua impressão digital, rosto ou bloqueio de ecrã.',
      title: 'Utilizar a sua chave de acesso',
    },
    password: {
      actionLink: 'Utilizar outro método',
      subtitle: 'Introduza a sua palavra-passe atual para continuar',
      title: 'Verificação necessária',
    },
    phoneCode: {
      formTitle: 'Código de verificação',
      resendButton: 'Não recebeu um código? Reenviar',
      subtitle: 'Introduza o código enviado para o seu telemóvel para continuar',
      title: 'Verificação necessária',
    },
    phoneCodeMfa: {
      formTitle: 'Código de verificação',
      resendButton: 'Não recebeu um código? Reenviar',
      subtitle: 'Introduza o código enviado para o seu telemóvel para continuar',
      title: 'Verificação necessária',
    },
    totpMfa: {
      formTitle: 'Código de verificação',
      subtitle: 'Introduza o código gerado pela sua aplicação autenticadora para continuar',
      title: 'Verificação necessária',
    },
  },
  signIn: {
    accountSwitcher: {
      action__addAccount: 'Adicionar conta',
      action__signOutAll: 'Terminar sessão de todas as contas',
      subtitle: 'Selecione a conta com a qual pretende continuar.',
      title: 'Escolher uma conta',
    },
    alternativeMethods: {
      actionLink: 'Ajuda',
      actionText: 'Não tem nenhum destes métodos?',
      blockButton__backupCode: 'Utilize um código de backup',
      blockButton__emailCode: 'Enviar código para {{identifier}}',
      blockButton__emailLink: 'Enviar ligação para {{identifier}}',
      blockButton__passkey: 'Utilizar chave de acesso',
      blockButton__password: 'Iniciar sessão com palavra-passe',
      blockButton__phoneCode: 'Enviar código para {{identifier}}',
      blockButton__totp: 'Utilize o seu autenticador',
      getHelp: {
        blockButton__emailSupport: 'E-mail de suporte',
        content:
          'Se estiver com dificuldades para entrar na sua conta, envie-nos um e-mail e iremos ajudar-te a restaurar o acesso o mais rápido possível.',
        title: 'Ajuda',
      },
      subtitle: 'Está com dificuldades? Pode utilizar qualquer um destes métodos para iniciar sessão.',
      title: 'Utilize outro método',
    },
    alternativePhoneCodeProvider: {
      formTitle: 'Código de verificação',
      resendButton: 'Não recebeu um código? Reenviar',
      subtitle: 'para continuar em {{applicationName}}',
      title: 'Verifique o {{provider}}',
    },
    backupCodeMfa: {
      subtitle: 'para continuar em {{applicationName}}',
      title: 'Insira um código de backup',
    },
    emailCode: {
      formTitle: 'Código de verificação',
      resendButton: 'Reenviar código',
      subtitle: 'para continuar em {{applicationName}}',
      title: 'Verifique o seu e-mail',
    },
    emailCodeMfa: {
      formTitle: 'Verifique o seu e-mail',
      resendButton: 'Não recebeu um código? Reenviar',
      subtitle: 'para continuar em {{applicationName}}',
      title: 'Verifique o seu e-mail',
    },
    emailLink: {
      clientMismatch: {
        subtitle: 'O cliente não corresponde ao esperado. Tente novamente.',
        title: 'Erro de cliente',
      },
      expired: {
        subtitle: 'Retorne para a aba original para continuar',
        title: 'Esta ligação de verificação expirou',
      },
      failed: {
        subtitle: 'Retorne para a aba original para continuar',
        title: 'Esta ligação de verificação é inválida',
      },
      formSubtitle: 'Utilize a ligação enviada para o seu e-mail',
      formTitle: 'Link de verificação',
      loading: {
        subtitle: 'Será redirecionado em breve',
        title: 'A iniciar sessão...',
      },
      resendButton: 'Não recebeu uma ligação? Reenviar',
      subtitle: 'para continuar em {{applicationName}}',
      title: 'Verifique o seu e-mail',
      unusedTab: {
        title: 'Já pode fechar esta aba',
      },
      verified: {
        subtitle: 'Será redirecionado em breve',
        title: 'Sessão iniciada com sucesso',
      },
      verifiedSwitchTab: {
        subtitle: 'Regresse ao separador original para continuar',
        subtitleNewTab: 'Regresse ao novo separador que foi aberto para continuar',
        titleNewTab: 'Sessão iniciada noutro separador',
      },
    },
    emailLinkMfa: {
      formSubtitle: 'Utilize a ligação de verificação enviada para o seu e-mail',
      resendButton: 'Não recebeu a ligação? Reenviar',
      subtitle: 'para continuar em {{applicationName}}',
      title: 'Verifique o seu e-mail',
    },
    enterpriseConnections: {
      subtitle: 'Selecione a conta empresarial com a qual pretende continuar.',
      title: 'Escolha a sua conta empresarial',
    },
    forgotPassword: {
      formTitle: 'Código de redefinição de palavra-passe',
      resendButton: 'Não recebeu um código? Reenviar',
      subtitle: 'para redefinir a sua palavra-passe',
      subtitle_email: 'Primeiro, introduza o código enviado para o seu endereço de e-mail',
      subtitle_phone: 'Primeiro, introduza o código enviado para o seu telemóvel',
      title: 'Redefinir palavra-passe',
    },
    forgotPasswordAlternativeMethods: {
      blockButton__resetPassword: 'Repor a palavra-passe',
      label__alternativeMethods: 'Ou inicie sessão com outro método.',
      title: 'Esqueceu-se da palavra-passe?',
    },
    newDeviceVerificationNotice:
      'Está a iniciar sessão a partir de um novo dispositivo. Estamos a solicitar verificação para manter a sua conta segura.',
    noAvailableMethods: {
      message: 'Não foi possível iniciar sessão. Não existe qualquer método de autenticação disponível.',
      subtitle: 'Ocorreu um erro',
      title: 'Não foi possível iniciar sessão',
    },
    passkey: {
      subtitle: 'Utilize a sua chave de acesso para autenticação.',
      title: 'Autenticação com Chave de Acesso',
    },
    password: {
      actionLink: 'Utilize outro método',
      subtitle: 'para continuar em {{applicationName}}',
      title: 'Insira a sua palavra-passe',
    },
    passwordCompromised: {
      title: 'Palavra-passe comprometida',
    },
    passwordPwned: {
      title: 'Este password foi comprometido em uma violação de dados. Escolha outro por motivos de segurança.',
    },
    passwordUntrusted: {
      title: 'Palavra-passe não fiável',
    },
    phoneCode: {
      formTitle: 'Código de verificação',
      resendButton: 'Reenviar código',
      subtitle: 'para continuar em {{applicationName}}',
      title: 'Verifique o seu telemóvel',
    },
    phoneCodeMfa: {
      formTitle: 'Código de verificação',
      resendButton: 'Reenviar código',
      subtitle: 'Insira o código enviado para o seu número de telemóvel',
      title: 'Verifique o seu telemóvel',
    },
    resetPassword: {
      formButtonPrimary: 'Repor Palavra-passe',
      requiredMessage: 'Por motivos de segurança, é necessário redefinir a sua palavra-passe.',
      successMessage: 'A sua palavra-passe foi alterada com sucesso. A iniciar sessão, aguarde um momento.',
      title: 'Repor Palavra-passe',
    },
    resetPasswordMfa: {
      detailsLabel: 'Precisamos verificar a sua identidade antes de redefinir a palavra-passe.',
    },
    start: {
      actionLink: 'Registar-se',
      actionLink__join_waitlist: 'Juntar-se à lista de espera',
      actionLink__use_email: 'Usar e-mail',
      actionLink__use_email_username: 'Usar e-mail ou nome de utilizador',
      actionLink__use_passkey: 'Usar chave de acesso',
      actionLink__use_phone: 'Usar telemóvel',
      actionLink__use_username: 'Usar nome de utilizador',
      actionText: 'Não tem conta?',
      actionText__join_waitlist: 'Ainda não tem uma conta? Junte-se à lista de espera.',
      alternativePhoneCodeProvider: {
        actionLink: 'Utilizar outro método',
        label: 'Número de telemóvel {{provider}}',
        subtitle: 'Introduza o seu número de telemóvel para receber um código de verificação no {{provider}}.',
        title: 'Inicie sessão em {{applicationName}} com {{provider}}',
      },
      subtitle: 'para continuar em {{applicationName}}',
      subtitleCombined: 'para continuar em {{applicationName}}',
      title: 'Entrar',
      titleCombined: 'Continuar em {{applicationName}}',
    },
    totpMfa: {
      formTitle: 'Código de verificação',
      subtitle: 'Insira o código de verificação enviado para o seu dispositivo.',
      title: 'Verificação de duas etapas',
    },
    web3Solana: {
      subtitle: 'Selecione uma carteira abaixo para iniciar sessão',
      title: 'Iniciar sessão com Solana',
    },
  },
  signInEnterPasswordTitle: 'Insira a sua palavra-passe',
  signUp: {
    alternativePhoneCodeProvider: {
      resendButton: 'Não recebeu um código? Reenviar',
      subtitle: 'Introduza o código de verificação enviado para o seu {{provider}}',
      title: 'Verifique o seu {{provider}}',
    },
    continue: {
      actionLink: 'Entrar',
      actionText: 'Já possui uma conta?',
      subtitle: 'para continuar em {{applicationName}}',
      title: 'Preencha os campos ausentes',
    },
    emailCode: {
      formSubtitle: 'Introduza o código enviado para o seu e-mail',
      formTitle: 'Código de verificação',
      resendButton: 'Não recebeu o código? Reenviar',
      subtitle: 'para continuar em {{applicationName}}',
      title: 'Verifique o seu e-mail',
    },
    emailLink: {
      clientMismatch: {
        subtitle: 'Parece que houve um erro com a sua sessão. Tente novamente.',
        title: 'Erro de sessão',
      },
      formSubtitle: 'Utilize a ligação enviada para o seu e-mail',
      formTitle: 'Link de verificação',
      loading: {
        title: 'A registar...',
      },
      resendButton: 'Reenviar ligação',
      subtitle: 'para continuar em {{applicationName}}',
      title: 'Verifique o seu e-mail',
      verified: {
        title: 'Registo realizado com sucesso',
      },
      verifiedSwitchTab: {
        subtitle: 'Volte para a nova aba que foi aberta para continuar',
        subtitleNewTab: 'Volte para a aba anterior para continuar',
        title: 'E-mail verificado com sucesso',
      },
    },
    enterpriseConnections: {
      subtitle: 'Selecione a conta empresarial com a qual pretende continuar.',
      title: 'Escolha a sua conta empresarial',
    },
    legalConsent: {
      checkbox: {
        label__onlyPrivacyPolicy: 'Aceito a Política de Privacidade',
        label__onlyTermsOfService: 'Aceito os Termos de Serviço',
        label__termsOfServiceAndPrivacyPolicy:
          'Aceito os {{ termsOfServiceLink || link("Termos de Serviço") }} e a {{ privacyPolicyLink || link("Política de Privacidade") }}',
      },
      continue: {
        subtitle: 'Ao continuar, concorda com os termos acima.',
        title: 'Continuar',
      },
    },
    phoneCode: {
      formSubtitle: 'Introduza o código enviado para o seu telemóvel',
      formTitle: 'Código de verificação',
      resendButton: 'Não recebeu o código? Reenviar',
      subtitle: 'para continuar em {{applicationName}}',
      title: 'Verifique o seu telemóvel',
    },
    restrictedAccess: {
      actionLink: 'Contactar suporte',
      actionText: 'Precisa de ajuda?',
      blockButton__emailSupport: 'Enviar e-mail para suporte',
      blockButton__joinWaitlist: 'Junte-se à lista de espera',
      subtitle: 'O seu acesso está restrito. Para mais informações, entre em contacto connosco.',
      subtitleWaitlist: 'Estamos a aguardar a sua entrada na lista de espera. Agradecemos pela paciência.',
      title: 'Acesso Restrito',
    },
    start: {
      actionLink: 'Entrar',
      actionLink__use_email: 'Usar e-mail',
      actionLink__use_phone: 'Usar telemóvel',
      actionText: 'Já tem uma conta?',
      alternativePhoneCodeProvider: {
        actionLink: 'Utilizar outro método',
        label: 'Número de telemóvel {{provider}}',
        subtitle: 'Introduza o seu número de telemóvel para receber um código de verificação no {{provider}}.',
        title: 'Registe-se em {{applicationName}} com {{provider}}',
      },
      subtitle: 'para continuar em {{applicationName}}',
      subtitleCombined: 'para continuar em {{applicationName}}',
      title: 'Criar a sua conta',
      titleCombined: 'Criar a sua conta',
    },
    web3Solana: {
      subtitle: 'Selecione uma carteira abaixo para se registar',
      title: 'Registar-se com Solana',
    },
  },
  socialButtonsBlockButton: 'Continuar com {{provider|titleize}}',
  socialButtonsBlockButtonManyInView: 'Continuar com {{provider|titleize}}',
  taskChooseOrganization: {
    alerts: {
      organizationAlreadyExists:
        'Já existe uma organização para o nome da empresa detetado ({{organizationName}}) e {{organizationDomain}}. Adira por convite.',
    },
    chooseOrganization: {
      action__createOrganization: 'Criar nova organização',
      action__invitationAccept: 'Participar',
      action__suggestionsAccept: 'Solicitar participação',
      subtitle: 'Junte-se a uma organização existente ou crie uma nova',
      subtitle__createOrganizationDisabled: 'Junte-se a uma organização existente',
      suggestionsAcceptedLabel: 'Aprovação pendente',
      title: 'Escolha uma organização',
    },
    createOrganization: {
      formButtonReset: 'Cancelar',
      formButtonSubmit: 'Continuar',
      formFieldInputPlaceholder__name: 'A minha organização',
      formFieldInputPlaceholder__slug: 'a-minha-organizacao',
      formFieldLabel__name: 'Nome',
      formFieldLabel__slug: 'Slug',
      subtitle: 'Introduza os detalhes da sua organização para continuar',
      title: 'Configurar a sua organização',
    },
    organizationCreationDisabled: {
      subtitle: 'Contacte o administrador da sua organização para obter um convite.',
      title: 'Deve pertencer a uma organização',
    },
    signOut: {
      actionLink: 'Terminar sessão',
      actionText: 'Sessão iniciada como {{identifier}}',
    },
  },
  taskResetPassword: {
    formButtonPrimary: 'Repor palavra-passe',
    signOut: {
      actionLink: 'Terminar sessão',
      actionText: 'Sessão iniciada como {{identifier}}',
    },
    subtitle: 'A sua conta requer uma nova palavra-passe antes de poder continuar',
    title: 'Reponha a sua palavra-passe',
  },
  taskSetupMfa: {
    badge: 'Configuração da verificação em duas etapas',
    signOut: {
      actionLink: 'Terminar sessão',
      actionText: 'Sessão iniciada como {{identifier}}',
    },
    smsCode: {
      addPhone: {
        formButtonPrimary: 'Continuar',
        infoText:
          'Será enviada uma mensagem de texto com um código de verificação para este número de telemóvel. Podem aplicar-se custos de mensagens e dados.',
      },
      addPhoneNumber: 'Adicionar número de telemóvel',
      cancel: 'Cancelar',
      subtitle: 'Escolha o número de telemóvel que pretende utilizar para a verificação em duas etapas por código SMS',
      success: {
        finishButton: 'Continuar',
        message1:
          'A verificação em duas etapas está agora ativada. Ao iniciar sessão, terá de introduzir um código de verificação enviado para este número de telemóvel como passo adicional.',
        message2:
          'Guarde estes códigos de recuperação num local seguro. Se perder o acesso ao seu dispositivo de autenticação, poderá utilizá-los para iniciar sessão.',
        title: 'Verificação por código SMS ativada',
      },
      title: 'Adicionar verificação por código SMS',
      verifyPhone: {
        formButtonPrimary: 'Continuar',
        formTitle: 'Código de verificação',
        resendButton: 'Não recebeu um código? Reenviar',
        subtitle: 'Introduza o código de verificação enviado para',
        title: 'Verifique o seu número de telemóvel',
      },
    },
    start: {
      methodSelection: {
        phoneCode: 'Código SMS',
        totp: 'Aplicação autenticadora',
      },
      subtitle: 'Escolha o método que prefere utilizar para proteger a sua conta com uma camada extra de segurança',
      title: 'Configurar a verificação em duas etapas',
    },
    totpCode: {
      addAuthenticatorApp: {
        buttonAbleToScan__nonPrimary: 'Ler código QR em alternativa',
        buttonUnableToScan__nonPrimary: 'Não consegue ler o código QR?',
        formButtonPrimary: 'Continuar',
        formButtonReset: 'Cancelar',
        infoText__ableToScan:
          'Configure um novo método de início de sessão na sua aplicação autenticadora e leia o seguinte código QR para o associar à sua conta.',
        infoText__unableToScan:
          'Configure um novo método de início de sessão no seu autenticador e introduza a chave indicada abaixo.',
        inputLabel__unableToScan1:
          'Certifique-se de que as palavras-passe baseadas no tempo ou de utilização única estão ativadas e, em seguida, conclua a associação da sua conta.',
      },
      success: {
        finishButton: 'Continuar',
        message1:
          'A verificação em duas etapas está agora ativada. Ao iniciar sessão, terá de introduzir um código de verificação desta aplicação autenticadora como passo adicional.',
        message2:
          'Guarde estes códigos de recuperação num local seguro. Se perder o acesso ao seu dispositivo de autenticação, poderá utilizá-los para iniciar sessão.',
        title: 'Verificação por aplicação autenticadora ativada',
      },
      title: 'Adicionar aplicação autenticadora',
      verifyTotp: {
        formButtonPrimary: 'Continuar',
        formButtonReset: 'Cancelar',
        formTitle: 'Código de verificação',
        subtitle: 'Introduza o código de verificação gerado pela sua aplicação autenticadora',
        title: 'Adicionar aplicação autenticadora',
      },
    },
  },
  unstable__errors: {
    already_a_member_in_organization: 'Já é membro nesta organização.',
    avatar_file_size_exceeded:
      'O tamanho do ficheiro excede o limite máximo de 10 MB. Por favor, escolha um ficheiro mais pequeno.',
    avatar_file_type_invalid: 'Tipo de ficheiro não suportado. Por favor, carregue uma imagem JPG, PNG, GIF ou WEBP.',
    captcha_invalid:
      'Não foi possível concluir o registo devido a falhas nas validações de segurança. Atualize a página para tentar novamente ou contacte o suporte para obter ajuda adicional.',
    captcha_unavailable:
      'Não foi possível concluir o registo devido a uma falha na validação de bot. Atualize a página para tentar novamente ou contacte o suporte para obter ajuda adicional.',
    form_code_incorrect: 'Código incorreto.',
    form_email_address_blocked:
      'Não são suportados serviços de e-mail temporários. Utilize o seu endereço de e-mail habitual para criar uma conta.',
    form_identifier_exists__email_address: 'O endereço de e-mail já está em uso.',
    form_identifier_exists__phone_number: 'O número de telemóvel já está em uso.',
    form_identifier_exists__username: 'O nome de utilizador já está em uso.',
    form_identifier_not_found: 'Não foi possível encontrar uma conta com esses dados.',
    form_new_password_matches_current: 'A nova palavra-passe não pode ser igual à palavra-passe atual.',
    form_param_format_invalid: 'Formato de parâmetro inválido.',
    form_param_format_invalid__email_address: 'O endereço de e-mail deve ser válido.',
    form_param_format_invalid__phone_number: 'O número de telemóvel deve ser válido.',
    form_param_max_length_exceeded__first_name: 'O primeiro nome não deve exceder 256 caracteres.',
    form_param_max_length_exceeded__last_name: 'O apelido não deve exceder 256 caracteres.',
    form_param_max_length_exceeded__name: 'O nome não deve exceder 256 caracteres.',
    form_param_nil: 'Parâmetro não pode ser nulo.',
    form_param_type_invalid: 'Tipo de parâmetro inválido.',
    form_param_type_invalid__email_address: 'O endereço de e-mail tem um tipo inválido.',
    form_param_type_invalid__phone_number: 'O número de telemóvel tem um tipo inválido.',
    form_param_value_invalid: 'Valor de parâmetro inválido.',
    form_password_compromised__sign_in:
      'Esta palavra-passe foi comprometida numa violação de dados. Escolha outra para continuar.',
    form_password_incorrect: 'Palavra-passe incorreta.',
    form_password_length_too_short: 'A palavra-passe é muito curta.',
    form_password_not_strong_enough: 'A sua palavra-passe não é forte o suficiente.',
    form_password_or_identifier_incorrect:
      'A palavra-passe ou o endereço de e-mail está incorreto. Tente novamente ou utilize outro método.',
    form_password_pwned:
      'Esta palavra-passe foi encontrada como parte de uma violação e não pode ser usada, por favor, tente outra palavra-passe.',
    form_password_pwned__sign_in:
      'Esta palavra-passe foi encontrada como parte de uma violação e não pode ser utilizada para iniciar sessão. Escolha outra.',
    form_password_size_in_bytes_exceeded:
      'A sua palavra-passe excedeu o número máximo de bytes permitidos, por favor, encurte-a ou remova alguns caracteres especiais.',
    form_password_untrusted__sign_in: 'Esta palavra-passe não é considerada segura. Escolha outra para continuar.',
    form_password_validation_failed: 'Falha na validação da palavra-passe.',
    form_username_invalid_character: 'O nome de utilizador contém caracteres inválidos.',
    form_username_invalid_length: 'O nome de utilizador deve ter entre 3 e 50 caracteres.',
    form_username_needs_non_number_char: 'O seu nome de utilizador deve conter pelo menos um carácter não numérico.',
    identification_deletion_failed: 'Não pode eliminar o seu último identificador.',
    not_allowed_access:
      "O endereço de e-mail ou número de telemóvel não é permitido para registo. Isto pode dever-se à utilização de '+', '=', '#' ou '.' no endereço de e-mail, ao uso de um domínio associado a um serviço de e-mail temporário ou a uma exclusão explícita.",
    organization_domain_blocked: 'Este domínio organizacional está bloqueado.',
    organization_domain_common: 'Este domínio é demasiado comum para ser utilizado numa organização.',
    organization_domain_exists_for_enterprise_connection: 'Este domínio já existe para uma ligação empresarial.',
    organization_membership_quota_exceeded:
      'Atingiu o limite de associações a organizações, incluindo convites pendentes.',
    organization_minimum_permissions_needed: 'Não dispõe das permissões mínimas necessárias para executar esta ação.',
    organization_not_found_or_unauthorized: 'Já não é membro desta organização. Escolha outra ou crie uma nova.',
    organization_not_found_or_unauthorized_with_create_organization_disabled:
      'Já não é membro desta organização. Escolha outra.',
    passkey_already_exists: 'Já existe uma chave de acesso registada neste dispositivo.',
    passkey_not_supported: 'As chaves de acesso não são suportadas neste dispositivo.',
    passkey_pa_not_supported: 'As chaves de acesso não são suportadas neste autenticador de plataforma.',
    passkey_registration_cancelled: 'O registo da chave de acesso foi cancelado ou expirou.',
    passkey_retrieval_cancelled: 'A verificação da chave de acesso foi cancelada ou expirou.',
    passwordComplexity: {
      maximumLength: 'menos de {{length}} caracteres',
      minimumLength: '{{length}} ou mais caracteres',
      requireLowercase: 'uma letra minúscula',
      requireNumbers: 'um número',
      requireSpecialCharacter: 'um carácter especial',
      requireUppercase: 'uma letra maiúscula',
      sentencePrefix: 'A sua palavra-passe deve conter',
    },
    phone_number_exists: 'Este número de telemóvel já está em uso. Por favor, tente outro.',
    session_exists: 'Já está conectado.',
    web3_missing_identifier: 'Falta um identificador Web3. Tente novamente.',
    web3_signature_request_rejected: 'Rejeitou o pedido de assinatura. Tente novamente para continuar.',
    web3_solana_signature_generation_failed: 'Ocorreu um erro ao gerar a assinatura. Tente novamente para continuar.',
    zxcvbn: {
      couldBeStronger: 'A sua palavra-passe funciona, mas poderia ser mais forte. Tente adicionar mais caracteres.',
      goodPassword: 'A sua palavra-passe cumpre todos os requisitos necessários.',
      notEnough: 'A sua palavra-passe não é forte o suficiente.',
      suggestions: {
        allUppercase: 'Utilize apenas algumas letras maiúsculas, não todas.',
        anotherWord: 'Adicione palavras menos comuns.',
        associatedYears: 'Evite anos associados a si.',
        capitalization: 'Utilize letras maiúsculas além da primeira letra.',
        dates: 'Evite datas e anos associados a si.',
        l33t: "Evite substituições previsíveis de letras, como '@' por 'a'.",
        longerKeyboardPattern: 'Utilize padrões de teclado mais longos e altere várias vezes a direção da escrita.',
        noNeed: 'Pode criar palavras-passe fortes sem utilizar símbolos, números ou letras maiúsculas.',
        pwned: 'Se usar esta palavra-passe noutro lugar, deve mudá-la.',
        recentYears: 'Evite anos recentes.',
        repeated: 'Evite palavras e caracteres repetidos.',
        reverseWords: 'Evite utilizar palavras comuns escritas de "trás para frente".',
        sequences: 'Evite sequências comuns de caracteres.',
        useWords: 'Utilize várias palavras, mas evite expressões comuns.',
      },
      warnings: {
        common: 'Esta é uma palavra-passe frequentemente utilizada.',
        commonNames: 'Nomes e apelidos comuns são fáceis de adivinhar.',
        dates: 'Datas são fáceis de adivinhar.',
        extendedRepeat: 'Padrões de caracteres repetidos, como "abcabcabc" são fáceis de adivinhar.',
        keyPattern: 'Padrões curtos de teclado são fáceis de adivinhar.',
        namesByThemselves: 'Nomes ou apelidos são fáceis de adivinhar.',
        pwned: 'A sua palavra-passe foi exposta numa violação de dados na Internet.',
        recentYears: 'Anos recentes são fáceis de adivinhar.',
        sequences: 'Sequências comuns de caracteres, como "abc" são fáceis de adivinhar.',
        similarToCommon: 'Esta é semelhante a uma palavra-passe frequentemente utilizada.',
        simpleRepeat: 'Caracteres repetidos, como "aaa" são fáceis de adivinhar.',
        straightRow: 'Letras que vêm em sequência no teclado são fáceis de adivinhar.',
        topHundred: 'Esta é uma palavra-passe usada frequentemente.',
        topTen: 'Esta é uma palavra-passe muito usada.',
        userInputs: 'Não deve conter quaisquer dados pessoais ou relacionados com a página.',
        wordByItself: 'Palavras simples são fáceis de adivinhar.',
      },
    },
  },
  userButton: {
    action__addAccount: 'Adicionar conta',
    action__closeUserMenu: 'Fechar menu do utilizador',
    action__manageAccount: 'Configurar conta',
    action__openUserMenu: 'Abrir menu do utilizador',
    action__signOut: 'Terminar sessão',
    action__signOutAll: 'Terminar sessão de todas as contas',
  },
  userProfile: {
    apiKeysPage: {
      title: 'Chaves de API',
    },
    backupCodePage: {
      actionLabel__copied: 'Copiado!',
      actionLabel__copy: 'Copiar tudo',
      actionLabel__download: 'Download .txt',
      actionLabel__print: 'Imprimir',
      infoText1: 'Códigos de backup serão ativados para esta conta.',
      infoText2:
        'Guarde-os em segurança e não os partilhe. Pode gerar novos códigos de recuperação se suspeitar que tenham sido comprometidos.',
      subtitle__codelist: 'Guarde-os em segurança e não os partilhe.',
      successMessage:
        'Os códigos de recuperação foram ativados para esta conta. Pode utilizar um deles para iniciar sessão na sua conta caso perca o acesso ao seu dispositivo de autenticação. Cada código só pode ser utilizado uma vez.',
      successSubtitle:
        'Pode utilizar um deles para iniciar sessão na sua conta caso perca o acesso ao seu dispositivo de autenticação.',
      title: 'Adicionar código de backup para verificação',
      title__codelist: 'Códigos de backup',
    },
    billingPage: {
      paymentHistorySection: {
        empty: 'Sem histórico de pagamentos',
        notFound: 'Tentativa de pagamento não encontrada',
        tableHeader__amount: 'Montante',
        tableHeader__date: 'Data',
        tableHeader__status: 'Estado',
      },
      paymentMethodsSection: {
        actionLabel__default: 'Definir como predefinido',
        actionLabel__remove: 'Remover',
        add: 'Adicionar novo método de pagamento',
        addSubtitle: 'Adicione um novo método de pagamento à sua conta.',
        cancelButton: 'Cancelar',
        formButtonPrimary__add: 'Adicionar método de pagamento',
        formButtonPrimary__pay: 'Pagar {{amount}}',
        payWithTestCardButton: 'Pagar com cartão de teste',
        removeMethod: {
          messageLine1: '{{identifier}} será removido desta conta.',
          messageLine2:
            'Deixará de poder utilizar esta fonte de pagamento e quaisquer subscrições recorrentes que dela dependam deixarão de funcionar.',
          successMessage: '{{paymentMethod}} foi removido da sua conta.',
          title: 'Remover método de pagamento',
        },
        title: 'Métodos de pagamento',
      },
      start: {
        headerTitle__payments: 'Pagamentos',
        headerTitle__plans: 'Planos',
        headerTitle__statements: 'Extratos',
        headerTitle__subscriptions: 'Subscrição',
      },
      statementsSection: {
        empty: 'Sem extratos para apresentar',
        itemCaption__paidForPlan: 'Pago pelo plano {{plan}} {{period}}',
        itemCaption__proratedCredit: 'Crédito proporcional por utilização parcial da subscrição anterior',
        itemCaption__subscribedAndPaidForPlan: 'Subscrição e pagamento do plano {{plan}} {{period}}',
        notFound: 'Extrato não encontrado',
        tableHeader__amount: 'Montante',
        tableHeader__date: 'Data',
        title: 'Extratos',
        totalPaid: 'Total pago',
      },
      subscriptionsListSection: {
        actionLabel__manageSubscription: 'Gerir',
        actionLabel__newSubscription: 'Subscrever um plano',
        actionLabel__switchPlan: 'Mudar de plano',
        tableHeader__edit: 'Editar',
        tableHeader__plan: 'Plano',
        tableHeader__startDate: 'Data de início',
        title: 'Subscrição',
      },
      subscriptionsSection: {
        actionLabel__default: 'Gerir',
      },
      switchPlansSection: {
        title: 'Mudar de plano',
      },
      title: 'Faturação',
    },
    connectedAccountPage: {
      formHint: 'Selecione um fornecedor para associar à sua conta.',
      formHint__noAccounts: 'Não existem fornecedores de conta externos disponíveis.',
      removeResource: {
        messageLine1: '{{identifier}} será removido desta conta.',
        messageLine2: 'Não vai conseguir usar esta conta e, quaisquer recursos dependentes dela deixarão de funcionar.',
        successMessage: '{{connectedAccount}} foi removido da sua conta.',
        title: 'Remover conta associada',
      },
      socialButtonsBlockButton: 'Associar conta {{provider|titleize}}',
      successMessage: 'O fornecedor foi adicionado à sua conta.',
      title: 'Associar uma conta',
    },
    deletePage: {
      actionDescription: 'Escreva Eliminar conta abaixo para continuar.',
      confirm: 'Eliminar conta',
      messageLine1:
        'Tem a certeza de que pretende eliminar a sua conta? Alguns dados associados poderão ser retidos. Para solicitar a eliminação completa de dados, contacte o suporte.',
      messageLine2: 'Esta ação é permanente e irreversível.',
      title: 'Eliminar conta',
    },
    emailAddressPage: {
      emailCode: {
        formHint: 'Será enviado um e-mail com um código de verificação para este endereço de e-mail.',
        formSubtitle: 'Insira o código de verificação enviado para {{identifier}}',
        formTitle: 'Código de verificação',
        resendButton: 'Não recebeu um código? Reenviar',
        successMessage: 'O e-mail {{identifier}} foi adicionado à sua conta.',
      },
      emailLink: {
        formHint: 'Será enviado um e-mail com uma ligação de verificação para este endereço de e-mail.',
        formSubtitle: 'Clique na ligação de verificação enviada para {{identifier}}',
        formTitle: 'Ligação de verificação',
        resendButton: 'Não recebeu um código? Reenviar',
        successMessage: 'O e-mail {{identifier}} foi adicionado à sua conta.',
      },
      enterpriseSSOLink: {
        formButton: 'Clique para autenticar',
        formSubtitle: 'Complete a autenticação com {{identifier}}',
      },
      formHint: 'Terá de verificar este endereço de e-mail antes de o poder adicionar à sua conta.',
      removeResource: {
        messageLine1: '{{identifier}} será removido desta conta.',
        messageLine2: 'Deixará de poder iniciar sessão com este endereço de e-mail.',
        successMessage: '{{emailAddress}} foi removido da sua conta.',
        title: 'Remover e-mail',
      },
      title: 'Adicionar e-mail',
      verifyTitle: 'Verificar endereço de e-mail',
    },
    formButtonPrimary__add: 'Adicionar',
    formButtonPrimary__continue: 'Continuar',
    formButtonPrimary__finish: 'Finalizar',
    formButtonPrimary__remove: 'Remover',
    formButtonPrimary__save: 'Guardar',
    formButtonReset: 'Cancelar',
    mfaPage: {
      formHint: 'Selecione um método para adicionar.',
      title: 'Adicione verificação de duas etapas',
    },
    mfaPhoneCodePage: {
      backButton: 'Utilizar número existente',
      primaryButton__addPhoneNumber: 'Adicionar número de telemóvel',
      removeResource: {
        messageLine1: '{{identifier}} deixará de receber códigos de verificação ao iniciar sessão.',
        messageLine2: 'A sua conta pode ficar menos segura. Tem a certeza de que pretende continuar?',
        successMessage: 'Código SMS de verificação de duas etapas foi removido para {{mfaPhoneCode}}',
        title: 'Remover verificação de duas etapas',
      },
      subtitle__availablePhoneNumbers:
        'Selecione um número de telemóvel para registar a verificação em duas etapas por código SMS.',
      subtitle__unavailablePhoneNumbers:
        'Não existem números de telemóvel disponíveis para registar a verificação em duas etapas por código SMS.',
      successMessage1:
        'Ao iniciar sessão, terá de introduzir um código de verificação enviado para este número de telemóvel como passo adicional.',
      successMessage2:
        'Guarde estes códigos de recuperação num local seguro. Se perder o acesso ao seu dispositivo de autenticação, poderá utilizá-los para iniciar sessão.',
      successTitle: 'Verificação por código SMS ativada',
      title: 'Adicionar verificação por SMS',
    },
    mfaTOTPPage: {
      authenticatorApp: {
        buttonAbleToScan__nonPrimary: 'Ler código QR em vez disso',
        buttonUnableToScan__nonPrimary: 'Não pode ler o código QR?',
        infoText__ableToScan:
          'Configure um novo método de início de sessão no seu autenticador e leia o seguinte código QR para o associar à sua conta.',
        infoText__unableToScan:
          'Configure um novo método de início de sessão no seu autenticador e introduza a chave indicada abaixo.',
        inputLabel__unableToScan1:
          "Certifique-se de que a opção 'One-time passwords' está ativa e, em seguida, conclua a associação da sua conta.",
        inputLabel__unableToScan2:
          'Alternativamente, se o seu autenticador suportar URIs TOTP, também pode copiar a URI completa.',
      },
      removeResource: {
        messageLine1: 'Os códigos de verificação deste autenticador deixarão de ser necessários ao iniciar sessão.',
        messageLine2: 'A sua conta pode ficar menos segura. Tem a certeza de que pretende continuar?',
        successMessage: 'A verificação de duas etapas via autenticador foi removida.',
        title: 'Remover verificação de duas etapas',
      },
      successMessage:
        'A verificação de duas etapas está agora ativa. Ao iniciar sessão, terá de introduzir um código de verificação deste autenticador como passo adicional.',
      title: 'Adicionar um autenticador',
      verifySubtitle: 'Insira o código de verificação gerado pelo seu autenticador',
      verifyTitle: 'Código de verificação',
    },
    mobileButton__menu: 'Menu',
    navbar: {
      account: 'Perfil',
      apiKeys: 'Chaves de API',
      billing: 'Faturação',
      description: 'Gira as informações da sua conta.',
      security: 'Segurança',
      title: 'Conta',
    },
    passkeyScreen: {
      removeResource: {
        messageLine1: 'Tem a certeza de que deseja remover este recurso?',
        title: 'Remover Recurso',
      },
      subtitle__rename: 'Altere o nome do recurso, se necessário.',
      title__rename: 'Renomear Recurso',
    },
    passwordPage: {
      checkboxInfoText__signOutOfOtherSessions:
        'Recomenda-se que termine sessão em todos os outros dispositivos que possam ter utilizado a sua palavra-passe antiga.',
      readonly:
        'A sua palavra-passe não pode ser editada porque só pode iniciar sessão através da ligação empresarial.',
      successMessage__set: 'A sua palavra-passe foi guardada.',
      successMessage__signOutOfOtherSessions: 'Terminou sessão em todos os outros dispositivos.',
      successMessage__update: 'A sua palavra-passe foi atualizada.',
      title__set: 'Defina a palavra-passe',
      title__update: 'Trocar palavra-passe',
    },
    phoneNumberPage: {
      infoText: 'Será enviada uma mensagem SMS com uma ligação de verificação para este telemóvel.',
      removeResource: {
        messageLine1: '{{identifier}} será removido desta conta.',
        messageLine2: 'Deixará de poder iniciar sessão com este número de telemóvel.',
        successMessage: '{{phoneNumber}} foi removido da sua conta.',
        title: 'Remover telemóvel',
      },
      successMessage: '{{identifier}} foi adicionado à sua conta.',
      title: 'Adicionar telemóvel',
      verifySubtitle: 'Introduza o código de verificação enviado para {{identifier}}',
      verifyTitle: 'Verificar número de telemóvel',
    },
    plansPage: {
      title: 'Planos',
    },
    profilePage: {
      fileDropAreaHint: 'Carregue uma imagem JPG, PNG, GIF ou WEBP menor que 10MB',
      imageFormDestructiveActionSubtitle: 'Remover imagem',
      imageFormSubtitle: 'Carregar imagem',
      imageFormTitle: 'Imagem de perfil',
      readonly: 'As informações do perfil foram fornecidas pela ligação empresarial e não podem ser editadas.',
      successMessage: 'O perfil foi atualizado.',
      title: 'Atualizar perfil',
    },
    start: {
      activeDevicesSection: {
        destructiveAction: 'Terminar sessão',
        title: 'Dispositivos ativos',
      },
      connectedAccountsSection: {
        actionLabel__connectionFailed: 'Tentar novamente',
        actionLabel__reauthorize: 'Reautorizar agora',
        destructiveActionTitle: 'Remover',
        primaryButton: 'Associar conta',
        subtitle__disconnected: 'Esta conta foi desligada. Clique abaixo para a associar novamente.',
        subtitle__reauthorize:
          'Os âmbitos necessários foram atualizados e poderá estar a experienciar funcionalidades limitadas. Reautorize esta aplicação para evitar quaisquer problemas.',
        title: 'Contas conectadas',
      },
      dangerSection: {
        deleteAccountButton: 'Eliminar conta',
        title: 'Perigo',
      },
      emailAddressesSection: {
        destructiveAction: 'Remover e-mail',
        detailsAction__nonPrimary: 'Definir como principal',
        detailsAction__primary: 'Completar verificação',
        detailsAction__unverified: 'Completar verificação',
        primaryButton: 'Adicionar um e-mail',
        title: 'Endereços de e-mail',
      },
      enterpriseAccountsSection: {
        primaryButton: 'Associar conta',
        title: 'Contas corporativas',
      },
      headerTitle__account: 'Conta',
      headerTitle__security: 'Segurança',
      mfaSection: {
        backupCodes: {
          actionLabel__regenerate: 'Gerar novos códigos',
          headerTitle: 'Códigos de backup',
          subtitle__regenerate:
            'Obter um novo conjunto de códigos de backup seguros. Os códigos de backup anteriores serão excluídos e não poderão ser utilizados novamente.',
          title__regenerate: 'Gerar novos códigos de backup',
        },
        phoneCode: {
          actionLabel__setDefault: 'Definir como principal',
          destructiveActionLabel: 'Remover telemóvel',
        },
        primaryButton: 'Adicione verificação',
        title: 'Verificação de duas etapas',
        totp: {
          destructiveActionTitle: 'Remover',
          headerTitle: 'Autenticador',
        },
      },
      passkeysSection: {
        menuAction__destructive: 'Remover chave de acesso',
        menuAction__rename: 'Renomear chave de acesso',
        primaryButton: 'Adicionar uma chave de acesso',
        title: 'Chaves de Acesso',
      },
      passwordSection: {
        primaryButton__setPassword: 'Definir palavra-passe',
        primaryButton__updatePassword: 'Atualizar palavra-passe',
        title: 'Palavra-passe',
      },
      phoneNumbersSection: {
        destructiveAction: 'Remover telemóvel',
        detailsAction__nonPrimary: 'Definir como principal',
        detailsAction__primary: 'Completar verificação',
        detailsAction__unverified: 'Completar verificação',
        primaryButton: 'Adicione um telemóvel',
        title: 'Números de telemóvel',
      },
      profileSection: {
        primaryButton: 'Guardar alterações',
        title: 'Perfil',
      },
      usernameSection: {
        primaryButton__setUsername: 'Definir nome de utilizador',
        primaryButton__updateUsername: 'Trocar nome de utilizador',
        title: 'Nome de utilizador',
      },
      web3WalletsSection: {
        destructiveAction: 'Remover carteira',
        detailsAction__nonPrimary: 'Definir como principal',
        primaryButton: 'Carteiras Web3',
        title: 'Carteiras Web3',
        web3SelectSolanaWalletScreen: {
          subtitle: 'Selecione uma carteira Solana para ligar à sua conta.',
          title: 'Adicionar uma carteira Solana',
        },
      },
    },
    usernamePage: {
      successMessage: 'O nome de utilizador foi atualizado.',
      title__set: 'Atualizar nome de utilizador',
      title__update: 'Atualizar nome de utilizador',
    },
    web3WalletPage: {
      removeResource: {
        messageLine1: '{{identifier}} será removido desta conta.',
        messageLine2: 'Não vai conseguir usar esta carteira Web3 para entrar na sua conta.',
        successMessage: '{{Web3Wallet}} foi removido da sua conta.',
        title: 'Remover carteira Web3',
      },
      subtitle__availableWallets: 'Selecione uma carteira Web3 para associar à sua conta.',
      subtitle__unavailableWallets: 'Não há carteiras Web3 disponíveis.',
      successMessage: 'A carteira foi adicionada à sua conta.',
      title: 'Adicionar carteira Web3',
      web3WalletButtonsBlockButton: 'Associar carteira Web3',
    },
  },
  waitlist: {
    start: {
      actionLink: 'Junte-se à lista de espera',
      actionText: 'Ainda não tem uma conta? Junte-se à lista de espera.',
      formButton: 'Submeter',
      subtitle: 'Deixe os seus dados para se juntar à lista de espera.',
      title: 'Juntar-se à lista de espera',
    },
    success: {
      message: 'Obrigado por se inscrever! Será notificado assim que tiver acesso.',
      subtitle: 'Aguarde enquanto processamos o seu pedido.',
      title: 'Inscrição bem-sucedida na lista de espera',
    },
  },
  web3SolanaWalletButtons: {
    connect: 'Associar com {{walletName}}',
    continue: 'Continuar com {{walletName}}',
    noneAvailable:
      'Não foram detetadas carteiras Solana Web3. Instale uma {{ solanaWalletsLink || link("wallet extension") }} com suporte Web3.',
  },
} as const;
